/**
 * CopyAppDialog.tsx — 复制 App 弹窗（注册名 copy-app）
 *
 * 移植自原型 design/Novel-admin/src/dialogs.jsx 的 CopyAppDialog（546–668 行）：
 * 从现有 App 复制运营配置生成新 App（默认暂停、Banner 停用；Web 专属配置复制，全局内容共用）。
 * 机械转换：JS→TS（加类型）、window.X 全局 → ESM import；弹窗外壳改用 ~/ui/Dialog 的 Dialog（受控 onClose）。
 * 已接真（goal apps）：「开始复制」→ POST /api/v1/apps/:id/copy（CopyAppRequest）；
 *   成功 invalidate ['apps'] + toast + 关弹窗，失败错误 toast 不关弹窗（不假成功）。
 * 字段映射（弹窗 → 契约 CopyAppRequest）：source.id→path :id · name→name · slug→slug。
 * 原型向导仅收名称，契约必填 slug → 补 Slug 输入行（与 NewAppWizard 同款补法）。
 * 视觉层（className / inline-style）与原型 1:1 保真。
 */

import { type CopyAppRequest, CopyAppRequestSchema } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { AppRow } from '~/data/queries';
import { copyApp } from '~/hooks/use-apps';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { toast } from '~/ui/Toast';

/** 复制 App 弹窗 props：source 为被复制的源 App，onClose 关闭回调 */
interface CopyAppDialogProps {
  source?: AppRow;
  onClose: () => void;
}

/** AdminApiError / Error → 用户可读文案 */
function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : '操作失败';
}

export function CopyAppDialog({ source, onClose }: CopyAppDialogProps) {
  // 为副本后缀预留长度；截断时保留完整 UTF-16 代理对，避免长 emoji 名称出现半个字符。
  const [name, setName] = useState(
    `${(source?.name || 'Rivmoon')
      .slice(0, 123)
      .replace(/[\uD800-\uDBFF]$/u, '')
      .trimEnd()} Copy`,
  );
  const [slug, setSlug] = useState(() => {
    // 展示名允许空格和中文，预填值仍须满足 Slug 的字符及 64 字符上限，运营可继续修改。
    const base = (source?.name || 'rivmoon')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 59)
      .replace(/-+$/g, '');
    return `${base || 'app'}-copy`;
  });
  // Link Schema 必填、不可复用源值（dev-docs#428 终态）：copy 继承源 platform → 新旧 App 同平台，而 scheme
  // 同平台内唯一 → 必须填新值。跨平台兄弟行（共用 scheme）经「新建 App」显式选平台建、不走复制。
  const [linkScheme, setLinkScheme] = useState('');
  const queryClient = useQueryClient();
  // 复制 App → POST /api/v1/apps/:id/copy；成功 invalidate ['apps'] + toast + 关弹窗，失败报错不关
  const copyMutation = useMutation({
    mutationFn: ({ sourceAppId, body }: { sourceAppId: string; body: CopyAppRequest }) =>
      copyApp(sourceAppId, body),
    onSuccess: (dto) => {
      toast(`已复制 App ${dto.name} · ${dto.slug} · 默认已暂停`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      onClose();
    },
    onError: (err) => toast(`复制失败 · ${errMsg(err)}`, { tone: 'neg', icon: 'IconAlert' }),
  });
  const handleCopy = () => {
    if (!source) {
      toast('复制失败 · 缺少源 App', { tone: 'neg', icon: 'IconAlert' });
      return;
    }
    const parsed = CopyAppRequestSchema.safeParse({
      name: name.trim(),
      slug: slug.trim(),
      // 必填、不可复用源值（同平台唯一）；留空 → Zod 必填校验拦
      app_link_scheme: source.platform === 'WEB' ? null : linkScheme.trim(),
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      toast(`校验未通过 · ${issue ? `${issue.path.join('.')}: ${issue.message}` : '请检查表单'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      return;
    }
    copyMutation.mutate({ sourceAppId: source.id, body: parsed.data });
  };
  return (
    <Dialog
      width={560}
      title="复制 App"
      subtitle={`从 ${source?.name || 'Rivmoon'} 复制配置 · 生成新 App ID`}
      icon="IconCopy"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            新 App 将处于「已暂停」状态，确认无误后再开启
          </div>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button className="btn btn-accent" disabled={copyMutation.isPending} onClick={handleCopy}>
            <Icons.IconCopy size={11} />
            {copyMutation.isPending ? '复制中…' : '开始复制'}
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow
          label="新 App 名称"
          required
          labelWidth={140}
          gap={14}
          pad="12px 0"
          labelPadTop={6}
        >
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </FormRow>
        {/* 契约必填 slug（CopyAppRequest），原型向导缺失 → 补输入行（与 NewAppWizard 同款补法） */}
        <FormRow label="新 Slug" required labelWidth={140} gap={14} pad="12px 0" labelPadTop={6}>
          <input
            className="input mono"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="如 aurora-copy"
          />
        </FormRow>
        {/* Link Schema 必填、不可复用源值：copy 继承源平台 → 同平台内 scheme 须唯一（dev-docs#428）*/}
        <FormRow
          label="Link Schema"
          required={source?.platform !== 'WEB'}
          labelWidth={140}
          gap={14}
          pad="12px 0"
          labelPadTop={6}
        >
          {source?.platform === 'WEB' ? (
            <span className="chip">WEB 使用 HTTPS 链接</span>
          ) : (
            <input
              className="input mono"
              value={linkScheme}
              onChange={(e) => setLinkScheme(e.target.value)}
              placeholder="必填 · 不可与原 App 相同（同平台内唯一）· 如 aurora-copy"
            />
          )}
        </FormRow>
        <FormRow label="平台" labelWidth={140} gap={14} pad="12px 0" labelPadTop={6}>
          <span className="chip">{source?.platform || 'iOS'} · 与原 App 一致</span>
        </FormRow>

        <div style={{ paddingTop: 14 }}>
          <div className="hstack" style={{ marginBottom: 10, gap: 8 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icons.IconCopy size={14} />
            </div>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 500 }}>自动复制的配置</div>
              <div
                style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2, lineHeight: 1.5 }}
              >
                以下内容与原 App 保持一致 · 复制后可在 App 详情页继续调整
              </div>
            </div>
          </div>
          <div
            className="card-flat"
            style={{ background: 'var(--bg-subtle)', padding: '8px 12px' }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '4px 16px',
                fontSize: 11.5,
                color: 'var(--fg-muted)',
              }}
            >
              {[
                '运营地区 & 允许语言集合',
                '千字单价默认值 & BONUS 比例',
                '任务奖励配置与八项开放排序',
                '激励广告默认参数与语言独立配置',
                '首页推荐区块',
                '支付方式策略与图标',
                ...(source?.platform === 'WEB' ? ['金币 / VIP 商品目录（停用草稿）'] : []),
              ].map((it) => (
                <div key={it} className="hstack" style={{ gap: 5 }}>
                  <Icons.IconCheck size={10} style={{ color: 'var(--pos)', flexShrink: 0 }} />
                  <span>{it}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 新 App 的身份与集成需重新绑定；暂停状态下允许运营逐项确认配置。 */}
          <div className="hstack" style={{ marginTop: 14, marginBottom: 10, gap: 8 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: 'var(--warn-soft)',
                color: 'var(--warn)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icons.IconEdit size={14} />
            </div>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 500 }}>上线前需确认</div>
              <div
                style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2, lineHeight: 1.5 }}
              >
                新 App 默认暂停，支付集成和 App 级书籍配置需在上线前单独确认
              </div>
            </div>
          </div>
          <div
            className="card-flat"
            style={{
              background: 'var(--warn-soft)',
              padding: '8px 12px',
              borderColor: 'transparent',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px 16px',
                fontSize: 11.5,
                color: 'var(--fg)',
              }}
            >
              {[
                { label: '书籍上下架配置', hint: 'App 级上下架覆盖 · 默认跟随全量状态' },
                // ADM-APP-S019（AB §5）：包体代号全局唯一、标识具体包体，复制不继承
                { label: '激励广告单元', hint: '登记全部清空 · 需为新 App 重新登记两用途单元' },
                { label: '包体代号', hint: '不随复制继承 · 新 App 为空，需在详情单独填写' },
                ...(source?.platform === 'WEB'
                  ? [{ label: 'Stripe 集成', hint: '需为新 App 单独配置并通过连接验证' }]
                  : [
                      {
                        label: 'Adapty placement',
                        hint: '需确认新 App 的 Adapty 集成及 placement',
                      },
                    ]),
              ].map((it) => (
                <div key={it.label} className="hstack" style={{ gap: 5, alignItems: 'flex-start' }}>
                  <Icons.IconCircle
                    size={9}
                    style={{ color: 'var(--warn)', flexShrink: 0, marginTop: 4 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{it.label}</div>
                    <div
                      style={{
                        fontSize: 10.5,
                        color: 'var(--fg-muted)',
                        marginTop: 1,
                        lineHeight: 1.4,
                      }}
                    >
                      {it.hint}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className="card-flat"
            style={{
              marginTop: 10,
              padding: '10px 12px',
              borderLeft: '2px solid var(--warn)',
              background: 'var(--warn-soft)',
            }}
          >
            <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
              <Icons.IconImg
                size={13}
                style={{ color: 'var(--warn)', flexShrink: 0, marginTop: 1 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--fg)' }}>
                  Banner 位 · 默认不启用
                </div>
                <div
                  style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2, lineHeight: 1.5 }}
                >
                  原 App 的 Banner 位会被一并创建（含轮播图、跳转目标、多语言），但{' '}
                  <span style={{ fontWeight: 500, color: 'var(--fg)' }}>状态默认为「下架」</span>
                  ，需手动确认后再启用，避免误投放。
                </div>
              </div>
            </div>
          </div>

          <div
            className="card-flat"
            style={{
              marginTop: 10,
              padding: '10px 12px',
              borderLeft: '2px solid var(--accent)',
              background: 'var(--accent-soft)',
            }}
          >
            <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
              <Icons.IconGlobe
                size={13}
                style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--fg)' }}>
                  {source?.platform === 'WEB'
                    ? '全局内容共用 · 专属 FAQ 复制'
                    : '全局 FAQ 与推送模板共用'}
                </div>
                <div
                  style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2, lineHeight: 1.5 }}
                >
                  全局 FAQ 与推送模板由各 App 共用，无需重复生成。
                  {source?.platform === 'WEB'
                    ? '源 App 的专属 FAQ、首页和书架推荐按各语言独立配置复制，空覆盖也保留。金币 / VIP 商品及完整设备变体复制为停用草稿，保留 Product / Price 映射；需重新配置 Stripe、核验后逐变体上架。'
                    : ''}
                </div>
              </div>
            </div>
          </div>

          <div
            className="card-flat"
            style={{ marginTop: 6, padding: '10px 12px', background: 'var(--bg-subtle)' }}
          >
            <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
              <Icons.IconLock
                size={12}
                style={{ color: 'var(--fg-muted)', flexShrink: 0, marginTop: 2 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--fg-muted)' }}>
                  不会被复制的数据
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>
                  用户数据 · 充值订单 · 退款记录 · 反馈工单 · 操作日志
                  {source?.platform === 'WEB' ? ' · Stripe 连接配置与凭据' : ''}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
